/* eslint-disable react-refresh/only-export-components */
import { Children, useEffect, type ReactNode } from "react"
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"
import { MenuButton } from "@/components/ui/menu"
import { cn } from "@/lib/utils"
import { MONTH_NAMES } from "@/lib/format"

/**
 * Blocos de página do design system. Extraídos do painel para que todas as telas tenham a mesma
 * hierarquia: cabeçalho com título e ações, blocos com borda fina e listas divididas por linhas.
 */

export function Page({ title, children, className, wide = true }: { title: string; children: ReactNode; className?: string; wide?: boolean }) {
  useEffect(() => { document.title = `${title} · FinControl` }, [title])
  return (
    <div className={cn("px-4 md:px-8 py-6 md:py-8 space-y-6", wide ? "max-w-[1320px]" : "max-w-[880px]", className)}>
      {children}
    </div>
  )
}

export function PageHeader({ title, description, actions, leading }: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  leading?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="min-w-0 flex-1 flex items-start gap-3">
        {leading}
        <div className="min-w-0">
          <h1 className="text-[22px] md:text-[26px] font-semibold tracking-[-0.02em] leading-tight">{title}</h1>
          {description && <p className="text-muted-foreground mt-1 text-[14px]">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:justify-end">{actions}</div>}
    </div>
  )
}

export const panelClass = "rounded-lg border bg-card min-w-0"
export const panelPadding = "px-5 md:px-6"

export function Panel({ title, description, action, children, footer, className, id, bodyClassName, as: Tag = "section" }: {
  title?: ReactNode
  description?: ReactNode
  action?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  className?: string
  bodyClassName?: string
  id?: string
  as?: "section" | "div"
}) {
  const titleId = id ? `${id}-titulo` : undefined
  return (
    <Tag className={cn(panelClass, className)} aria-labelledby={title ? titleId : undefined}>
      {title && (
        <div className={cn("flex items-center justify-between gap-3 py-4 border-b", panelPadding)}>
          <div className="min-w-0">
            <h2 id={titleId} className="font-semibold text-[15px] tracking-[-0.01em]">{title}</h2>
            {description && <p className="text-[13px] text-muted-foreground mt-0.5">{description}</p>}
          </div>
          {action && <div className="shrink-0 flex items-center gap-2">{action}</div>}
        </div>
      )}
      {children !== undefined && <div className={bodyClassName}>{children}</div>}
      {footer && (
        <div className={cn("py-3 border-t bg-background/40 rounded-b-lg text-[13px]", panelPadding)}>{footer}</div>
      )}
    </Tag>
  )
}

export type Tone = "positive" | "negative" | "warning" | "info" | "neutral"

export const toneText: Record<Tone, string> = {
  positive: "text-positive-700 dark:text-positive-400",
  negative: "text-negative-600 dark:text-negative-400",
  warning: "text-warning-700 dark:text-warning-400",
  info: "text-info-700 dark:text-info-300",
  neutral: "text-foreground",
}

/** Faixa de indicadores: um bloco só, dividido por linhas finas (sem um cartão por número). */
export function StatGrid({ children, className, columns = 4 }: { children: ReactNode; className?: string; columns?: 2 | 3 | 4 | 5 }) {
  const count = Children.toArray(children).filter(Boolean).length
  const lg = { 2: "lg:grid-cols-2", 3: "lg:grid-cols-3", 4: "lg:grid-cols-4", 5: "lg:grid-cols-5" }[columns]
  return (
    <section
      aria-label="Indicadores"
      className={cn(
        "grid grid-cols-2 gap-px bg-border border rounded-lg overflow-hidden",
        lg,
        count % 2 === 1 && "[&>*:first-child]:col-span-2 lg:[&>*:first-child]:col-span-1",
        className,
      )}
    >
      {children}
    </section>
  )
}

export function Stat({ label, value, hint, tone = "neutral", hintTone }: {
  label: ReactNode
  value: ReactNode
  hint?: ReactNode
  tone?: Tone
  hintTone?: Tone
}) {
  return (
    <div className="bg-card px-4 md:px-5 py-4 min-w-0">
      <p className="text-[12px] md:text-[13px] text-muted-foreground truncate">{label}</p>
      <p className={cn("num text-[18px] md:text-[22px] font-semibold tracking-[-0.02em] mt-1 truncate", toneText[tone])}>{value}</p>
      {hint && <p className={cn("text-[12px] mt-0.5 truncate", hintTone ? toneText[hintTone] : "text-muted-foreground")}>{hint}</p>}
    </div>
  )
}

const progressTone: Record<Tone, string> = {
  positive: "bg-positive-700 dark:bg-positive-500",
  negative: "bg-negative-600",
  warning: "bg-warning-600",
  info: "bg-info-600",
  neutral: "bg-neutral-400 dark:bg-neutral-600",
}

export function Progress({ value, tone = "positive", label, className }: { value: number; tone?: Tone; label?: string; className?: string }) {
  const v = Math.min(Math.max(value, 0), 100)
  return (
    <div
      className={cn("h-1.5 rounded-sm bg-inset overflow-hidden", className)}
      {...(label ? { role: "progressbar", "aria-label": label, "aria-valuenow": Math.round(v), "aria-valuemin": 0, "aria-valuemax": 100 } : { role: "presentation" })}
    >
      <div className={cn("h-full", progressTone[tone])} style={{ width: `${v}%` }} />
    </div>
  )
}

/** Tom da barra de uso: até 80% ok, até 100% atenção, acima estourado. */
export function usageTone(percent: number): Tone {
  return percent > 100 ? "negative" : percent >= 80 ? "warning" : "positive"
}

const badgeTone: Record<Tone, string> = {
  positive: "bg-positive-50 text-positive-800 dark:bg-positive-950 dark:text-positive-200",
  negative: "bg-negative-50 text-negative-800 dark:bg-negative-950 dark:text-negative-200",
  warning: "bg-warning-50 text-warning-900 dark:bg-warning-950 dark:text-warning-200",
  info: "bg-info-50 text-info-800 dark:bg-info-950 dark:text-info-200",
  neutral: "bg-inset text-foreground/80",
}

export function Badge({ tone = "neutral", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] font-medium whitespace-nowrap", badgeTone[tone], className)}>
      {children}
    </span>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("bg-inset animate-pulse rounded-md", className)} />
}

export function LoadingRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className={cn("py-5 space-y-3", panelPadding)} aria-busy="true" aria-label="Carregando">
      {Array.from({ length: rows }, (_, i) => <Skeleton key={i} className="h-10" />)}
    </div>
  )
}

export function EmptyState({ icon, title, description, action, className }: {
  icon?: ReactNode
  title: ReactNode
  description?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div className={cn("py-12 px-6 text-center flex flex-col items-center", className)}>
      {icon && <div className="h-10 w-10 rounded-lg bg-inset flex items-center justify-center text-muted-foreground mb-3 [&_svg]:h-5 [&_svg]:w-5">{icon}</div>}
      <p className="font-medium text-[15px]">{title}</p>
      {description && <p className="text-[14px] text-muted-foreground mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

export function MonthStepper({ month, year, onChange, disableFuture = false }: {
  month: number
  year: number
  onChange: (month: number, year: number) => void
  disableFuture?: boolean
}) {
  const now = new Date()
  const atCurrent = month === now.getMonth() + 1 && year === now.getFullYear()
  const prev = () => (month === 1 ? onChange(12, year - 1) : onChange(month - 1, year))
  const next = () => (month === 12 ? onChange(1, year + 1) : onChange(month + 1, year))
  return (
    <div className="flex items-center rounded-md border bg-card h-9">
      <button type="button" onClick={prev} aria-label="Mês anterior" className="h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="text-[13px] font-medium min-w-[118px] text-center" aria-live="polite">{MONTH_NAMES[month - 1]} {year}</span>
      <button type="button" onClick={next} disabled={disableFuture && atCurrent} aria-label="Próximo mês"
        className="h-9 w-9 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30">
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  )
}

/** Alternância entre opções (abas leves, filtros). O estado vai em aria-pressed. */
export function Segmented<T extends string | number>({ options, value, onChange, label, size = "md", className }: {
  options: ReadonlyArray<{ value: T; label: ReactNode }>
  value: T
  onChange: (value: T) => void
  label: string
  size?: "sm" | "md"
  className?: string
}) {
  return (
    <div role="group" aria-label={label} className={cn("inline-flex rounded-md border bg-inset p-0.5 gap-0.5 max-w-full overflow-x-auto", className)}>
      {options.map(o => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-[5px] whitespace-nowrap font-medium transition-colors flex items-center gap-1.5",
            size === "sm" ? "h-7 px-2.5 text-[12px]" : "h-8 px-3 text-[13px]",
            value === o.value ? "bg-card text-foreground ring-1 ring-border" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Abas de seção com sublinhado (Configurações, Calculadoras). Botões com aria-pressed. */
export function TabBar<T extends string | number>({ tabs, value, onChange, label }: {
  tabs: ReadonlyArray<{ value: T; label: ReactNode; icon?: ReactNode }>
  value: T
  onChange: (value: T) => void
  label: string
}) {
  return (
    <div role="group" aria-label={label} className="flex gap-1 border-b overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
      {tabs.map(t => (
        <button
          key={String(t.value)}
          type="button"
          aria-pressed={value === t.value}
          onClick={() => onChange(t.value)}
          className={cn(
            "flex items-center gap-1.5 px-3 h-10 text-[14px] font-medium border-b-2 -mb-px whitespace-nowrap shrink-0 transition-colors [&_svg]:h-4 [&_svg]:w-4",
            value === t.value ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          {t.icon}
          {t.label}
        </button>
      ))}
    </div>
  )
}

/** Linha de lista "rótulo ........ valor" usada em resumos. */
export function DataRow({ label, value, strong, className }: { label: ReactNode; value: ReactNode; strong?: boolean; className?: string }) {
  return (
    <div className={cn("flex justify-between gap-3 text-[14px]", strong && "font-medium", className)}>
      <dt className={strong ? "" : "text-foreground/75"}>{label}</dt>
      <dd className="num text-right">{value}</dd>
    </div>
  )
}

/** Quadrado colorido com ícone ou emoji (contas, cartões, categorias, metas). */
export function ColorMark({ color, children, size = "md" }: { color: string; children?: ReactNode; size?: "sm" | "md" | "lg" }) {
  const dims = { sm: "h-7 w-7 text-[14px]", md: "h-9 w-9 text-[17px]", lg: "h-11 w-11 text-[20px]" }[size]
  return (
    <span
      className={cn("rounded-md shrink-0 flex items-center justify-center text-white [&_svg]:h-4 [&_svg]:w-4", dims)}
      style={{ backgroundColor: color }}
      aria-hidden="true"
    >
      {children}
    </span>
  )
}

/** Botão só com ícone para ações de linha (editar, excluir). title mantém o tooltip nativo. */
export function RowAction({ label, onClick, children, tone = "neutral", disabled }: {
  label: string
  onClick: () => void
  children: ReactNode
  tone?: "neutral" | "negative"
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "h-8 w-8 rounded-md flex items-center justify-center transition-colors disabled:opacity-40 [&_svg]:h-4 [&_svg]:w-4",
        tone === "negative"
          ? "text-muted-foreground hover:text-negative-700 hover:bg-negative-50 dark:hover:bg-negative-950/50 dark:hover:text-negative-300"
          : "text-muted-foreground hover:text-foreground hover:bg-inset",
      )}
    >
      {children}
    </button>
  )
}

export function Notice({ tone = "info", children, action, icon }: { tone?: Tone; children: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  const tones: Record<Tone, string> = {
    info: "border-info-200 bg-info-50 text-info-900 dark:border-info-900 dark:bg-info-950/40 dark:text-info-200",
    warning: "border-warning-200 bg-warning-50 text-warning-900 dark:border-warning-900 dark:bg-warning-950/40 dark:text-warning-200",
    negative: "border-negative-200 bg-negative-50 text-negative-800 dark:border-negative-900 dark:bg-negative-950/40 dark:text-negative-200",
    positive: "border-positive-200 bg-positive-50 text-positive-900 dark:border-positive-900 dark:bg-positive-950/40 dark:text-positive-200",
    neutral: "border-border bg-inset text-foreground/80",
  }
  return (
    <div role={tone === "negative" ? "alert" : "status"} className={cn("rounded-lg border px-4 md:px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between text-[13px]", tones[tone])}>
      <p className="flex items-start gap-2 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:mt-0.5 [&_svg]:shrink-0">{icon}<span>{children}</span></p>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}

export interface RowActionItem {
  label: string
  /** Texto curto do menu no celular; o rótulo completo continua como nome acessível no desktop. */
  menuLabel?: string
  icon: ReactNode
  onClick: () => void
  tone?: "neutral" | "negative"
  disabled?: boolean
}

/** Ações de uma linha: ícones lado a lado a partir de sm; no celular, um menu "Mais ações" para não espremer o conteúdo. */
export function RowActions({ actions, label = "Mais ações", className }: { actions: RowActionItem[]; label?: string; className?: string }) {
  return (
    <div className={cn("flex shrink-0 -mr-2", className)}>
      <div className="hidden sm:flex">
        {actions.map((a) => <RowAction key={a.label} label={a.label} tone={a.tone} disabled={a.disabled} onClick={a.onClick}>{a.icon}</RowAction>)}
      </div>
      <div className="sm:hidden">
        <MenuButton
          ariaLabel={label}
          variant="ghost"
          size="icon"
          chevron={false}
          icon={<MoreHorizontal className="h-4 w-4" />}
          className="h-8 w-8"
          items={actions.map((a) => ({ label: a.menuLabel ?? a.label, icon: a.icon, onSelect: a.onClick, disabled: a.disabled }))}
        />
      </div>
    </div>
  )
}
